import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { PccAuthService } from '@ss/shared/auth';
import { SnackbarService } from '@ss/shared/snackbar';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, defaultIfEmpty, switchMap } from 'rxjs/operators';
@Injectable()
export class PccTokenInterceptor implements HttpInterceptor {

	private pccAuthService = inject(PccAuthService);

	private snackbar = inject(SnackbarService);

	intercept(
		request: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		if (request.url.includes('skipPccAuth=true')) {
			return next.handle(request);
		}

		const isAdrApiPath = request.url.includes('/adr/');
		if (!request.url.includes('pcc') && !isAdrApiPath) {
			return next.handle(request);
		}

		if (isAdrApiPath) {
			const token = this.pccAuthService.getToken();
			if (!token?.accessToken) {
				return next.handle(request).pipe(catchError((error: HttpErrorResponse) => this.handleError(error, request, next)));
			}
			return this.pccAuthService.refreshTokenAndWait().pipe(
				defaultIfEmpty(token),
				switchMap((refreshedToken) => {
					let req = request;
					if (refreshedToken?.accessToken && refreshedToken?.isValid) {
						req = request.clone({ setHeaders: { 'pcc-auth': refreshedToken.accessToken, 'pcc-org-uuid': refreshedToken.orgUuid } });
					}
					return next.handle(req).pipe(catchError((error: HttpErrorResponse) => this.handleError(error, req, next)));
				}),
			);
		}

		return this.pccAuthService.refreshTokenAndWait().pipe(switchMap((token) => {
			if (token) {
				request = request.clone({ setHeaders: { 'pcc-auth': token.accessToken, 'pcc-org-uuid': token.orgUuid } });
				return next.handle(request).pipe(catchError((error: HttpErrorResponse) => this.handleError(error, request, next)));
			}
			return EMPTY;
		}));
	}

	private handleError(error: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandler): Observable<any> {
		let errorMessage = 'An unexpected error occurred. Please try again later.';

		switch (error.status) {
			/* eslint-disable @typescript-eslint/no-magic-numbers */
			case 400:
				errorMessage = 'Bad request. Please check your input and try again.';
				break;
			case 401:
				errorMessage = 'Your session has expired. Please log in again.';
				break;
			case 404:
				errorMessage = 'Requested resource not found. Please contact support if this persists.';
				break;
			case 429:
				errorMessage = error.message.includes('daily quota exceeded') ? 'Daily limit reached. Please try again tomorrow.' : 'Too many requests. Please wait a moment and try again.';
				break;
			case 500:
				errorMessage = 'Server error. Please try again later or contact support.';
				break;
			/* eslint-enable @typescript-eslint/no-magic-numbers */
		}

		this.snackbar.failure(errorMessage);
		console.error(`PointClickCare API Error: ${error.status} - ${error.message}`);
		return of({});
	}
}
